import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("kind");
  const { clear } = useCart();
  const [state, setState] = useState("loading"); // loading | paid | failed | card

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        if (kind === "card") {
          const { data } = await api.get(`/subscription/card-status/${sessionId}`);
          if (data.updated) { setState("card"); return; }
        } else {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setState("paid");
            clear();
            return;
          }
          if (data.status === "failed" || data.status === "expired") {
            setState("failed");
            return;
          }
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 30) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="safe-pt grid min-h-screen place-items-center bg-cream px-6">
      <div className="w-full max-w-md rounded-3xl border border-ink/10 bg-panel p-10 text-center" data-testid="payment-success-page">
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto mb-5 h-10 w-10 animate-spin text-electric" />
            <h1 className="font-serif text-3xl font-light text-ink">Conferma pagamento…</h1>
            <p className="mt-2 text-sm text-ink-soft">Stiamo verificando il tuo pagamento con Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-electric text-cream shadow-[0_0_30px_rgba(0,163,255,0.45)]">
              <Check className="h-7 w-7" strokeWidth={2} />
            </span>
            <h1 className="font-serif text-3xl font-light text-ink" data-testid="payment-paid-title">Pagamento riuscito!</h1>
            <p className="mt-2 text-sm text-ink-soft">Il tuo ordine è confermato. Ti abbiamo inviato un'email di riepilogo.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/account" data-testid="goto-account" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream hover:bg-electric-deep">Il mio account</Link>
              <Link to="/" className="rounded-full border border-ink/15 px-6 py-3 text-sm text-ink hover:bg-ink/5">Continua lo shopping</Link>
            </div>
          </>
        )}
        {state === "card" && (
          <>
            <span className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-electric text-cream shadow-[0_0_30px_rgba(0,163,255,0.45)]">
              <Check className="h-7 w-7" strokeWidth={2} />
            </span>
            <h1 className="font-serif text-3xl font-light text-ink" data-testid="card-updated-title">Carta aggiornata!</h1>
            <p className="mt-2 text-sm text-ink-soft">La nuova carta sarà usata per i prossimi rinnovi automatici del Box.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/account" data-testid="goto-account" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream hover:bg-electric-deep">Il mio account</Link>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto mb-5 h-10 w-10 text-terra" />
            <h1 className="font-serif text-3xl font-light text-ink">Pagamento non confermato</h1>
            <p className="mt-2 text-sm text-ink-soft">Qualcosa è andato storto. Se l'addebito risulta comunque, contattaci.</p>
            <Link to="/" className="mt-6 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream hover:bg-electric-deep">Torna al negozio</Link>
          </>
        )}
      </div>
    </div>
  );
}
